import { LearningMaterialSummary, useSearchLearningMaterialsFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { cloneElement, ReactElement, useRef, useState } from "react";
import { EventNames } from "core/monitoring/types/enums";
import { ReactComponent as CrossIcon } from "shared/assets/Icons/cross.svg";
import { ReactComponent as SearchIcon } from "@icons/magnifying-glass.svg";
import { t } from "i18next";
import { trackEvents } from "core/monitoring/Events";
import useComponentVisible from "shared/UI/Hooks/useComponentVisible";
import { useDebounce } from "shared/core/hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { Input } from "shared/UI/Input/Input";
import Spinner from "@brighthr/component-spinner";

export const SearchBar = (): ReactElement => {
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { targetAreaRef, isComponentVisible, setIsComponentVisible } = useComponentVisible<HTMLDivElement>(false);
  const navigate = useNavigate();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { data: searchResults, isLoading, error } = useSearchLearningMaterialsFunctionRequest(
    { maxResults: 15, text: debouncedSearchTerm },
    { shouldFetch: !!debouncedSearchTerm && debouncedSearchTerm.length > 2 }
  );

  const onTextChange = (text: string) => {
    setSearchTerm(text);
  };

  const onSearchResultClicked = (articleId: string) => {
    trackEvents(EventNames.NAV_SEARCH, { query: searchTerm, value: articleId });
    setIsComponentVisible(false);
    setSearchTerm("");
    navigate(`/articles/${articleId}`);
  };

  const getArticleSubText = (article: LearningMaterialSummary): string => {
    const articleTypeText = t<string>(`navigation.search.articleType-${article.articleType}`);
    const durationText = article.duration ? ` - ${t("navigation.search.articleDuration", { duration: article.duration })}` : "";
    return articleTypeText + durationText;
  };

  const clearSearchTerm = () => {
    setSearchTerm("");
    inputRef?.current && inputRef.current.focus();
  }

  let icon;

  if (isLoading) {
    icon = <Spinner size="xs" ariaLabel={t("navigation.search.searchInputLoadingAria")} className="animate-spin stroke-brand-purple" />;
  } else if (searchTerm !== "") {
    icon = <CrossIcon className="text-navy-blue-bright hover:text-navy-blue-light max-h-4 max-w-4" />;
  } else {
    icon = <SearchIcon />;
  }

  return (
    <>
      {!isComponentVisible ? (
        <button
          aria-label={t("navigation.search.searchButtonAriaLabel")}
          className="float-right h-full lg:mx-2 mt-0 align-middle text-brand"
          onClick={() => setIsComponentVisible(true)}
        >
          <SearchIcon title={t("navigation.search.searchBarIconLabel")} className="h-7 w-7" />
        </button>
      ) : (
        <div ref={targetAreaRef} className={classNames("absolute z-40 w-full align-middle bg-white flex flex-col justify-center")}>
          <div className="relative top-0 w-full">
            <div className="flex items-center w-full">
              <Input
                ref={inputRef}
                state={error ? "error" : undefined}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                placeholder={t("navigation.search.searchInputPlaceholder")}
                aria-label={t("navigation.search.searchInputLabel")}
                onChange={(e) => onTextChange(e.target.value)}
                onBlur={(e) => {
                  e.target.focus();
                }}
                value={searchTerm}
              />
              {cloneElement(icon, { ...icon.props, className: classNames("absolute z-50 right-0 mr-5 cursor-pointer h-6 w-6", icon.props.className), onClick: clearSearchTerm, title: t("navigation.search.searchBarIconLabel") })}
            </div>
          </div>
          <div className="searchbar-scrollbar rounded-b absolute -z-10 top-full -translate-y-2 bg-l-blue w-full max-h-[50vh] overflow-y-auto shadow-md">
            {searchResults?.map((result) => (
              <button
                title={t<string>(`navigation.search.articleType-${result.articleType}`)}
                key={result.id}
                className="first:pt-5 cursor-pointer border-b last:border-none border-navy-blue-lighter block w-full py-3 px-4 text-left hover:bg-navy-blue-lighter active:bg-navy-blue-lighter focus:bg-navy-blue-lighter"
                aria-label={t("navigation.search.searchResultLabel", { result: result.title })}
                onClick={() => onSearchResultClicked(result.id)}
              >
                <p className="font-bold">{result.title}</p>
                <p>{getArticleSubText(result)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
